import Close from '@ixigo/icons/Close';
import Button from '@ixigo/iui/Button';
import Typography from '@ixigo/iui/Typography';
import { useState } from 'react';
import { NavigationHeader } from '@/components';
import { CabPicker, getCabAttributes } from '@/components/cab-selection';
import type { RescheduleCabOptions } from '../../../-api/reschedule';
import { tripTracker } from '../../../-utils/tracker';

interface RescheduleCabSelectionProps {
	cabSelection: RescheduleCabOptions;
	note: string;
	selectedCabId?: string;
	parentBookingId?: string;
	onClose: () => void;
	onConfirm: (cabId: string) => void;
}

export function RescheduleCabSelection({
	cabSelection,
	note,
	selectedCabId: initialSelectedCabId,
	parentBookingId,
	onClose,
	onConfirm,
}: RescheduleCabSelectionProps) {
	const { currentCabId } = cabSelection;
	const [selectedCabId, setSelectedCabId] = useState(
		initialSelectedCabId || currentCabId || '',
	);
	const selectedCab = cabSelection.options.find(
		(cab) => cab.cabId === selectedCabId,
	);
	const effectiveNote = selectedCab?.ctaNote ?? note;

	return (
		<div className="flex flex-col">
			<NavigationHeader
				title="Choose a cab"
				leftIcon={<Close />}
				leftAction={onClose}
				className="border-b border-tertiary sticky top-0 z-[1] bg-primary rounded-t-30"
			/>
			<CabPicker
				onChange={setSelectedCabId}
				cabSelectionOptions={cabSelection}
				selectedCabId={selectedCabId}
			>
				{(cab) =>
					cab.rescheduleDetails.refundSubtext ? (
						<Typography
							variant="body-2xs"
							className="text-success text-right max-w-24"
							// biome-ignore lint/security/noDangerouslySetInnerHtml: refundSubtext is safe html string
							dangerouslySetInnerHTML={{
								__html: cab.rescheduleDetails.refundSubtext,
							}}
						/>
					) : null
				}
			</CabPicker>
			<div className="sticky bottom-0 w-full bg-primary border-t border-tertiary px-20 py-10 space-y-1">
				<Button
					variant="solid"
					color="brand"
					size="lg"
					className="w-full"
					onClick={() => {
						if (selectedCabId && selectedCab) {
							tripTracker.track('Cab Type Selected', {
								...getCabAttributes(selectedCab),
								Funnel: 'Cab Reschedule',
								...(parentBookingId
									? { 'Parent Booking ID': parentBookingId }
									: {}),
							});
							onConfirm(selectedCabId);
						}
					}}
					disabled={!selectedCabId}
				>
					Confirm
				</Button>
				{effectiveNote && (
					<Typography variant="body-xs" className="text-secondary text-center">
						{effectiveNote}
					</Typography>
				)}
			</div>
		</div>
	);
}
